import { Checkbox } from '@/components/ui/checkbox';

export interface StatusFilterOption {
  value: string;
  label: string;
}

interface StatusFilterCheckboxesProps {
  options: StatusFilterOption[];
  /**
   * Currently-selected status values. EMPTY means "all" — every box renders
   * checked. This keeps a single canonical encoding (empty URL/state = no
   * filter = show everything) while presenting the intuitive "all on by
   * default" UI.
   */
  selected: string[];
  /**
   * Emits the next selection. The component canonicalizes: when the toggle
   * would select every option (or deselect the last one), it emits `[]` so
   * "all selected" and the stored "no filter" stay the same value.
   */
  onChange: (next: string[]) => void;
}

/**
 * Multi-select status filter rendered as checkboxes, so an admin can show e.g.
 * Open + In Progress at once instead of a single-choice dropdown. Starts with
 * every status checked (empty selection = all); unchecking one narrows the
 * view. Unchecking everything snaps back to "all" rather than showing nothing.
 */
export function StatusFilterCheckboxes({
  options,
  selected,
  onChange,
}: StatusFilterCheckboxesProps) {
  const allValues = options.map((o) => o.value);
  const isAll = selected.length === 0;
  const isChecked = (value: string) => isAll || selected.includes(value);

  const toggle = (value: string) => {
    const effective = isAll ? allValues : selected;
    const next = effective.includes(value)
      ? effective.filter((s) => s !== value)
      : [...effective, value];
    // "all selected" (and the degenerate "none selected") both canonicalize to
    // [] = all, so the stored value never ambiguously means two things.
    onChange(next.length === allValues.length ? [] : next);
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className="flex cursor-pointer select-none items-center gap-1.5 text-sm"
        >
          <Checkbox checked={isChecked(opt.value)} onCheckedChange={() => toggle(opt.value)} />
          {opt.label}
        </label>
      ))}
    </div>
  );
}
